import { useRef, useState } from 'react';
import { motion, AnimatePresence, useScroll, useTransform } from 'framer-motion';
import { ArrowDown, CalendarCheck, Sparkles, PhoneCall } from 'lucide-react';
import { useLang } from '../i18n';
import LlamamosModal from './LlamamosModal';

const HERO_IMG =
    'https://static.prod-images.emergentagent.com/jobs/c312d019-e509-40a5-b411-a1f75defbc2a/images/5f58fec4e4ff3209af5fcb347f3c5692e86f0c670f6cb1a2b9b457f7246d2d81.jpeg';

export default function Hero({ onNav }) {
    const { t } = useLang();
    const [showLlamamos, setShowLlamamos] = useState(false);
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
    const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);
    const sealY = useTransform(scrollYProgress, [0, 1], [0, -50]);

    const METRICS = [
        { value: '+280%', label: t('metric_roi') },
        { value: '98%', label: t('metric_retencion') },
        { value: '150+', label: t('metric_proyectos') },
    ];

    return (
        <section
            id="inicio"
            ref={ref}
            className="relative overflow-hidden"
            style={{ background: 'linear-gradient(135deg, #E2F1F8 0%, #FFF8E7 48%, #FCE7F0 100%)' }}
            data-testid="hero-section"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center min-h-[92vh] pt-32 pb-20">
                    <div className="lg:col-span-6">
                        <motion.div
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7 }}
                            className="inline-flex items-center gap-2 rounded-full border border-ink/15 bg-white/60 backdrop-blur px-4 py-1.5"
                            data-testid="hero-eyebrow"
                        >
                            <Sparkles size={13} className="text-crimson" />
                            <span className="font-ink text-[10px] sm:text-[11px] uppercase tracking-[0.28em] text-ink/70">
                                {t('hero_eyebrow')}
                            </span>
                        </motion.div>

                        <h1 className="mt-7 font-wordmark font-semibold leading-[1.04] text-ink text-[clamp(2.2rem,5.6vw,4.3rem)]">
                            <span className="mask-line">
                                <motion.span
                                    initial={{ y: '112%' }}
                                    animate={{ y: 0 }}
                                    transition={{ duration: 0.9, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
                                >
                                    #PICHONTEAM
                                </motion.span>
                            </span>
                            <span className="mask-line">
                                <motion.span
                                    className="block text-crimson"
                                    initial={{ y: '112%' }}
                                    animate={{ y: 0 }}
                                    transition={{ duration: 0.9, delay: 0.32, ease: [0.22, 1, 0.36, 1] }}
                                >
                                    GROWTH PARTNERS
                                </motion.span>
                            </span>
                        </h1>

                        <motion.p
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.8, delay: 0.75 }}
                            className="mt-5 font-display italic text-xl sm:text-2xl text-ink/80"
                            data-testid="hero-tagline"
                        >
                            {t('hero_tagline')}
                        </motion.p>

                        <motion.p
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.85 }}
                            className="mt-5 max-w-xl text-base sm:text-lg leading-relaxed text-ink/75"
                            data-testid="hero-value-prop"
                        >
                            {t('hero_value')}
                        </motion.p>

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.95 }}
                            className="mt-9 flex flex-wrap items-center gap-x-4 gap-y-4"
                        >
                            <button
                                onClick={() => onNav('#agenda')}
                                className="group btn-shine inline-flex items-center gap-2 rounded-full bg-ink px-5 py-4 text-[12px] font-bold uppercase tracking-[0.12em] text-pastelcream transition-all hover:bg-crimson hover:scale-[1.03] active:scale-95"
                                data-testid="hero-agenda-cta"
                            >
                                <CalendarCheck size={16} />
                                {t('cta_agenda')}
                            </button>
                            <button
                                onClick={() => onNav('#servicios')}
                                className="ink-link text-[12px] font-bold uppercase tracking-[0.12em] text-ink"
                                data-testid="hero-servicios-link"
                            >
                                {t('cta_servicios')}
                            </button>
                            <button
                                onClick={() => setShowLlamamos(true)}
                                className="inline-flex items-center gap-2 rounded-full bg-crimson px-5 py-4 text-[12px] font-bold uppercase tracking-[0.12em] text-pastelcream transition-all hover:bg-crimsondark hover:scale-[1.03] active:scale-95"
                                data-testid="hero-llamamos-cta"
                            >
                                <PhoneCall size={16} />
                                {t('llamamos_btn')}
                            </button>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.9, delay: 1.1 }}
                            className="mt-12 grid grid-cols-3 gap-4 max-w-lg border-t border-ink/10 pt-6"
                            data-testid="hero-metrics"
                        >
                            {METRICS.map((m) => (
                                <div key={m.label} data-testid="hero-metric-item">
                                    <div className="font-display text-2xl sm:text-3xl font-semibold text-crimson">
                                        {m.value}
                                    </div>
                                    <div className="mt-1 text-[11px] uppercase tracking-[0.16em] text-ink/55 font-semibold">
                                        {m.label}
                                    </div>
                                </div>
                            ))}
                        </motion.div>
                    </div>

                    <div className="lg:col-span-6 relative mt-6 lg:mt-0">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.94 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 1.1, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
                            className="relative"
                        >
                            <motion.div style={{ y: imgY }} className="relative">
                                <div
                                    className="absolute -inset-4 rounded-[2rem] rotate-2"
                                    style={{ background: 'linear-gradient(135deg, rgba(226,241,248,0.9), rgba(252,231,240,0.9))' }}
                                />
                                <div className="relative rounded-[1.6rem] overflow-hidden border border-ink/15 shadow-[0_40px_80px_-30px_rgba(27,42,74,0.35)]">
                                    <img
                                        src={HERO_IMG}
                                        alt="Ilustración a tinta de dos niños en un columpio bajo un árbol, imagen de marca de #PICHONTEAM"
                                        className="w-full h-full object-cover"
                                        data-testid="hero-image"
                                    />
                                </div>
                            </motion.div>

                            <motion.div
                                style={{ y: sealY }}
                                className="w-fit ml-auto mt-4 sm:mt-0 sm:absolute sm:right-6 sm:top-[60%] flex items-center gap-3 sm:gap-4 rounded-2xl bg-white/90 backdrop-blur border border-ink/10 px-4 py-3 sm:px-5 sm:py-4 shadow-[0_24px_50px_-20px_rgba(27,42,74,0.4)]"
                                data-testid="hero-floating-badge"
                            >
                                <div className="font-wordmark text-2xl font-semibold text-ink leading-none">
                                    Pt
                                </div>
                                <div className="leading-tight text-left">
                                    <div className="text-[11px] font-bold uppercase tracking-[0.16em] text-ink">
                                        {t('hero_badge_t')}
                                    </div>
                                    <div className="text-[10px] font-ink tracking-[0.1em] text-crimson mt-0.5">
                                        #PICHONTEAM · GROWTH PARTNERS
                                    </div>
                                </div>
                            </motion.div>
                        </motion.div>
                    </div>
                </div>
            </div>

            <motion.button
                onClick={() => onNav('#historia')}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.4 }}
                className="absolute bottom-6 left-1/2 -translate-x-1/2 text-ink/50 hover:text-crimson transition-colors"
                aria-label="Bajar a Nuestra Historia"
                data-testid="hero-scroll-hint"
            >
                <ArrowDown size={20} className="animate-bounce" />
            </motion.button>

            <AnimatePresence>
                {showLlamamos && <LlamamosModal onClose={() => setShowLlamamos(false)} />}
            </AnimatePresence>
        </section>
    );
}
